import type {
  AdminTokenAccessMode,
  AdminTokenModule,
  CreateAdminTokenReply,
  CreateAdminTokenRequest,
  ListAdminTokensReply,
  RevokeAdminTokenReply,
} from '@/types';
import {
  normalizeToken,
  type RawAdminToken,
  validateCreatedToken,
} from './adminTokenContract';
import api from './api';

type RawCreateAdminTokenReply = {
  token?: RawAdminToken;
  tokenValue?: string;
  token_value?: string;
};

type RawListAdminTokensReply = {
  tokens?: RawAdminToken[];
  supported_access_modes?: string[];
  supportedAccessModes?: string[];
  supported_modules?: string[];
  supportedModules?: string[];
};

type RawRevokeAdminTokenReply = {
  token?: RawAdminToken;
};

export const adminTokenApi = {
  createToken: async (
    data: CreateAdminTokenRequest,
  ): Promise<CreateAdminTokenReply> => {
    const response = (await api.post('/api/v1/admin/tokens', {
      note: data.note,
      expires_in_days: data.expiresInDays,
      access_mode: data.accessMode,
      module_scope: {
        grants: data.moduleGrants.map((grant) => ({
          module: grant.module,
          access_mode: grant.accessMode,
        })),
      },
    })) as RawCreateAdminTokenReply;
    return {
      token: validateCreatedToken(
        response.token ?? {},
        data.accessMode,
        data.moduleGrants,
      ),
      tokenValue: response.tokenValue ?? response.token_value ?? '',
    };
  },

  listTokens: async (): Promise<ListAdminTokensReply> => {
    const response = (await api.get(
      '/api/v1/admin/tokens',
    )) as RawListAdminTokensReply;
    return {
      tokens: (response.tokens ?? []).map(normalizeToken),
      supportedAccessModes: (
        response.supportedAccessModes ??
        response.supported_access_modes ??
        []
      ).filter(
        (mode): mode is AdminTokenAccessMode =>
          mode === 'read' || mode === 'write',
      ),
      supportedModules: (
        response.supportedModules ??
        response.supported_modules ??
        []
      ).filter((module): module is AdminTokenModule =>
        [
          'basic',
          'access',
          'commerce',
          'app',
          'llm',
          'release',
          'computer',
          'diagnostics',
          'acquisition',
          'room_capacity',
          'test_management',
        ].includes(module),
      ),
    };
  },

  revokeToken: async (tokenId: string): Promise<RevokeAdminTokenReply> => {
    const response = (await api.post(
      `/api/v1/admin/tokens/${encodeURIComponent(tokenId)}/revoke`,
      {},
    )) as RawRevokeAdminTokenReply;
    return {
      token: normalizeToken(response.token ?? {}),
    };
  },
};
